import Bottleneck from 'bottleneck';
import { z } from 'zod/v4';
export var ExportType;
(function (ExportType) {
    ExportType["Xlsx"] = "XLSX";
    ExportType["Csv"] = "CSV";
})(ExportType || (ExportType = {}));
export const ExportTypeSchema = z.object({
    exportType: z.enum(ExportType),
});
export var BankAccountType;
(function (BankAccountType) {
    BankAccountType["Danish"] = "DANISH";
})(BankAccountType || (BankAccountType = {}));
export var TimespanUnit;
(function (TimespanUnit) {
    TimespanUnit["Years"] = "years";
    TimespanUnit["Quarters"] = "quarters";
    TimespanUnit["Months"] = "months";
    TimespanUnit["Weeks"] = "weeks";
    TimespanUnit["Days"] = "days";
    TimespanUnit["Hours"] = "hours";
    TimespanUnit["Minutes"] = "minutes";
    TimespanUnit["Seconds"] = "seconds";
    TimespanUnit["MS"] = "ms";
})(TimespanUnit || (TimespanUnit = {}));
export var Currency;
(function (Currency) {
    Currency["DKK"] = "DKK";
    Currency["SEK"] = "SEK";
    Currency["NOK"] = "NOK";
    Currency["USD"] = "USD";
    Currency["EUR"] = "EUR";
    Currency["GBP"] = "GBP";
    Currency["CHF"] = "CHF";
    Currency["AUD"] = "AUD";
})(Currency || (Currency = {}));
export var CountryCode;
(function (CountryCode) {
    CountryCode["DK"] = "DK";
    CountryCode["GL"] = "GL";
    CountryCode["NO"] = "NO";
})(CountryCode || (CountryCode = {}));
export var Entity;
(function (Entity) {
    Entity["Debtor"] = "debtor";
    Entity["Case"] = "case";
    Entity["TribunalCase"] = "tribunal-case";
    Entity["Creditor"] = "creditor";
    Entity["Customer"] = "customer";
    Entity["Collector"] = "collector";
    Entity["CaseVoucher"] = "case-voucher";
    Entity["BillingVoucher"] = "billing-voucher";
    Entity["Billing"] = "billing";
})(Entity || (Entity = {}));
export var Site;
(function (Site) {
    Site["Caseworker"] = "caseworker";
    Site["Client"] = "client";
    Site["Debtor"] = "debtor";
})(Site || (Site = {}));
export var Gender;
(function (Gender) {
    Gender["MALE"] = "MALE";
    Gender["FEMALE"] = "FEMALE";
    Gender["BOTH"] = "BOTH";
})(Gender || (Gender = {}));
export const CurrencySchema = z.enum(Currency);
export const PhoneSchema = z.object({
    number: z.string(),
    locale: z.string(),
});
export const zodPhoneSchema = z.object({
    number: z.string(),
    locale: z.string(),
});
export const BankAccountDetailsSchema = z.object({
    type: z.enum(BankAccountType),
    registrationNumber: z.string(),
    accountNumber: z.string(),
});
export var PermissionLevels;
(function (PermissionLevels) {
    PermissionLevels["superAdmin"] = "super-admin";
    PermissionLevels["admin"] = "admin";
    PermissionLevels["caseworker"] = "caseworker";
    PermissionLevels["client"] = "client";
    PermissionLevels["debtor"] = "debtor";
    PermissionLevels["alternativeContact"] = "alternative-contact";
})(PermissionLevels || (PermissionLevels = {}));
export const AddressSchema = z.object({
    address: z.string(),
    coAddress: z.string().nullish(),
    zipcode: z.coerce.string(),
    city: z.string(),
    country: z.string().nullish(),
    KVHX: z.string().optional(),
    aksAddress: z.any().optional(),
});
export const addressToString = (address, config) => `${address.address}${address.coAddress && !config?.noCoAddress ? ` C/O ${address.coAddress}` : ''}, ${address.zipcode} ${address.city}${address.country && !config?.noCountry ? `, ${address.country}` : ''}`;
export const validateAddress = (address) => {
    return address && address.address && address.city && address.zipcode
        ? address.address.length > 0 &&
            address.city.length > 0 &&
            `${address.zipcode}`.length > 0
        : false;
};
export const compareAddresses = (addressA, addressB) => {
    return ((addressA ? addressToString(addressA).toLocaleLowerCase() : '') ===
        (addressB ? addressToString(addressB).toLocaleLowerCase() : ''));
};
export const validatePhone = (phone) => {
    return !!(phone && phone.locale && phone.number);
};
export const validateCPR = (cpr, withModulus) => {
    if (withModulus) {
        cpr = cpr?.replace(/[ \D]/gi, '');
        if (typeof cpr === 'undefined' || cpr === '' || cpr.length !== 10) {
            return false;
        }
        let mainInt = 0;
        const factors = [4, 3, 2, 7, 6, 5, 4, 3, 2, 1];
        for (let ciffer = 0; ciffer < cpr.length; ciffer++) {
            mainInt += parseInt(cpr.substr(ciffer, 1)) * factors[ciffer];
        }
        return mainInt % 11 === 0;
    }
    else {
        return !!(cpr && cpr.length === 10);
    }
};
export const validateCVR = (cvr) => {
    return !!(cvr && cvr.length === 8);
};
export var Messages;
(function (Messages) {
    Messages["CASE_NOT_FOUND"] = "You don't have access to this case";
})(Messages || (Messages = {}));
export const validateEmail = (_email) => {
    const email = _email ? _email : '';
    if (!email)
        return false;
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email.toLowerCase());
};
export const formatNumber = (n, separator = ',') => {
    if (separator === '.') {
        return (Math.round(n) / 100)
            .toFixed(2)
            .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }
    return (Math.round(n) / 100)
        .toFixed(2)
        .replace('.', ',')
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
};
const DEFAULT_CACHE_TIME = 1000 * 30;
const cachedItems = {};
export const cache = async (cacheTimeMs = DEFAULT_CACHE_TIME, loader, handle, forceFlush, source, ...params) => {
    const time = new Date().getTime();
    const paramsHandle = params.filter((p) => typeof p === 'string').join('-');
    const fullhandle = `${handle}${paramsHandle}`;
    if (forceFlush) {
        if (source && source.flush) {
            try {
                source.flush(fullhandle);
            }
            catch (err) {
                console.error(err);
            }
        }
        else {
            delete cachedItems[fullhandle];
        }
    }
    if (source && source.get && source.store) {
        let item;
        try {
            item = await source.get(fullhandle);
        }
        catch (err) { }
        if (item) {
            return item;
        }
        else {
            let data;
            try {
                data = await loader(...params);
            }
            catch (err) {
                throw err;
            }
            try {
                await source.store(fullhandle, data, cacheTimeMs);
            }
            catch (err) {
                console.error(err);
            }
            return data;
        }
    }
    else {
        if (cachedItems[fullhandle] && cachedItems[fullhandle].flushTime > time) {
            return cachedItems[fullhandle].data;
        }
        else {
            const data = await loader(...params);
            cachedItems[fullhandle] = {
                data,
                flushTime: time + cacheTimeMs,
            };
            return data;
        }
    }
};
const bns = {};
export const bottleneck = async (handle, minTime, f, concurrent) => new Promise((res, rej) => {
    if (!bns[handle]) {
        bns[handle] = new Bottleneck({
            maxConcurrent: concurrent,
            minTime: minTime,
        });
    }
    bns[handle].schedule(f).then(res).catch(rej);
});
export const DateIntervalSchema = z.union([
    z.object({ from: z.string().optional(), to: z.string() }),
    z.object({ from: z.string(), to: z.string().optional() }),
    z.object({ from: z.string(), to: z.string() }),
]);
const luhnChecksum = (code) => {
    const len = code.length;
    const parity = len % 2;
    let sum = 0;
    for (let i = len - 1; i >= 0; i--) {
        let d = parseInt(code.charAt(i));
        if (i % 2 == parity) {
            d *= 2;
        }
        if (d > 9) {
            d -= 9;
        }
        sum += d;
    }
    return sum % 10;
};
const encode = (partcode) => {
    const pcode = partcode.toString();
    var checksum = luhnChecksum(pcode + '0');
    var checksum = checksum == 0 ? 0 : 10 - checksum;
    const digit = pcode + checksum;
    return digit;
};
const decode = (s) => {
    const n = parseInt(s.slice(0, -1));
    if (s !== encode(n)) {
        throw 'Error in payment ID';
    }
    return n;
};
export const numberVal = {
    decode,
    encode,
};
export var Timezone;
(function (Timezone) {
    Timezone["EuropeCopenhagen"] = "Europe/Copenhagen";
    Timezone["EuropeOslo"] = "Europe/Oslo";
    Timezone["AmericaNuuk"] = "America/Nuuk";
})(Timezone || (Timezone = {}));
export const TagSchema = z.object({
    from: z.string().nullish(),
    to: z.string().nullish(),
    tag: z.string(),
});
export const BankAccountSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(BankAccountType.Danish),
        registrationNumber: z.string().length(4).regex(/^\d+$/),
        accountNumber: z.string().min(6).max(10).regex(/^\d+$/),
        bankName: z.string().optional(),
    }),
]);
export const BankAccountZodSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(BankAccountType.Danish),
        registrationNumber: z
            .string()
            .length(4)
            .regex(/^[0-9]*$/),
        accountNumber: z
            .string()
            .min(2)
            .max(10)
            .regex(/^[0-9]*$/),
    }),
]);
export const UpdateAccountSchema = z.object({
    bankAccountDetails: BankAccountSchema.nullish(),
    name: z.string().optional(),
    isRefund: z.boolean().optional(),
});
export const AccountingNumberSchema = z.object({
    number: z.string(),
});
export const collection = (items, page, pageSize) => {
    return {
        items,
        meta: {
            currentPage: page,
            pageSize,
        },
    };
};
//# sourceMappingURL=index.js.map